<template>
  <base-form-modal
    :title="$t('add_security')"
    :loading="loading"
    :textSave="$t('add')"
    hide-submit
    hide-cancel
  >
    <template #content>
      <ValidationObserver ref="formObserver" v-slot="{ handleSubmit }">
        <v-row>
          <v-col cols="11">
            <template v-if="!isNew">
              <ValidationProvider
                :name="$t('general_isin_code')"
                rules="required"
                v-slot="{ errors }"
                class="flex-grow-1"
              >
                <v-autocomplete
                  ref="securityInput"
                  v-model="securitySelected"
                  :search-input.sync="search"
                  item-text="isinCode"
                  item-value="isinCode"
                  :items="securities"
                  :label="`${$t('general_isin_code')} *`"
                  :filter="filterSecurities"
                  :error-messages="errors[0]"
                  hide-details="auto"
                  dense
                  return-object
                  auto-select-first
                  autofocus
                  @change="handleSecurityChange"
                >
                  <template #item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title v-html="item.name" />
                      <v-list-item-subtitle v-html="item.isinCode" />
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
              </ValidationProvider>
            </template>
            <template v-else>
              <ValidationProvider
                :name="$t('general_isin_code')"
                rules="required|length:12"
                v-slot="{ errors }"
              >
                <v-text-field
                  v-if="isNew"
                  ref="isinInput"
                  v-model="security.isin"
                  :label="`${$t('general_isin_code')} *`"
                  :error-messages="errors[0]"
                  hide-details="auto"
                  maxlength="12"
                  dense
                  autofocus
                />
              </ValidationProvider>
            </template>
          </v-col>
          <v-col cols="1">
            <v-tooltip top>
              <template #activator="{ on }">
                <v-btn
                  color="primary"
                  min-width="32"
                  min-height="32"
                  width="32"
                  height="32"
                  icon
                  outlined
                  v-on="on"
                  :disabled="isNew"
                  @click="handleNewSecurity"
                >
                  <v-icon>mdi-plus</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('security_transfer_create_security') }}</span>
            </v-tooltip>
          </v-col>
          <v-col cols="12">
            <ValidationProvider
              :name="$t('general_name')"
              rules="required|max:60"
              v-slot="{ errors }"
            >
              <v-text-field
                ref="name"
                :label="`${$t('general_name')} *`"
                dense
                autocomplete="nope"
                v-model="security.name"
                :error-messages="errors[0]"
                :disabled="!isNew"
                hide-details="auto"
                maxlength="60"
              />
            </ValidationProvider>
          </v-col>
          <v-col cols="12" md="6">
            <ValidationProvider
              :name="`${$t('general_quantity')} *`"
              rules="required|numeric|min_value:1"
              v-slot="{ errors, validate }"
            >
              <NumericInput
                v-model="security.quantity"
                :label="`${$t('general_quantity')} *`"
                :error-messages="errors[0]"
                :options="{
                  precision: { min: 0, max: 0 },
                  currency: 'USD',
                  currencyDisplay: 'hidden'
                }"
                hide-details="auto"
                dense
                @blur="validate"
              />
            </ValidationProvider>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <div class="d-flex justify-space-between">
              <v-btn outlined @click="handleClose">
                {{ $t('btn_cancel') }}
              </v-btn>
              <v-btn
                @click="handleSubmit(handleAccept)"
                color="secondary"
                elevation="0"
                :loading="loading"
              >
                {{ $t('btn_save') }}
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </ValidationObserver>
    </template>
  </base-form-modal>
</template>

<script>
import BaseFormModal from '@/components/commons/BaseFormModal'
import NumericInput from '@/components/commons/NumericInput'

export default {
  components: { BaseFormModal, NumericInput },
  props: {
    securities: {
      type: Array,
      required: true
    },
    editSecurity: {
      type: Object,
      required: false,
      default: null
    }
  },
  data() {
    return {
      isNew: false,
      search: null,
      securitySelected: null,
      security: {
        name: null,
        isin: null,
        description: null,
        quantity: null,
        price: null
      },
      loading: false
    }
  },
  mounted() {
    if (this.editSecurity) {
      this.isNew =
        this.securities.findIndex(
          item => item.isinCode === this.editSecurity.isin
        ) === -1

      this.securitySelected = {
        ...this.editSecurity,
        isinCode: this.editSecurity.isin
      }

      this.security = {
        name: this.editSecurity.name,
        isin: this.editSecurity.isin,
        description: this.editSecurity.description,
        quantity: this.editSecurity.quantity,
        price: this.editSecurity.price
      }
    }

    this.$refs.formObserver.reset()
  },
  methods: {
    filterSecurities(item, queryText) {
      if (!queryText) {
        return true
      }

      const text = queryText.toLowerCase()
      const name = item.name ? item.name.toLowerCase() : ''
      const isinCode = item.isinCode ? item.isinCode.toLowerCase() : ''

      return name.indexOf(text) > -1 || isinCode.indexOf(text) > -1
    },
    handleNewSecurity() {
      this.isNew = true
      this.securitySelected = null
      this.security = {
        name: null,
        isin: null,
        description: null,
        quantity: null,
        price: null
      }

      this.$nextTick(() => {
        this.$refs.isinInput.focus()
      })
    },
    handleAccept() {
      this.$emit('submit', this.security)
    },
    handleClose() {
      this.$emit('submit', false)
    },
    handleSecurityChange() {
      if (this.securitySelected) {
        this.security.name = this.securitySelected.name
        this.security.isin = this.securitySelected.isinCode
      } else {
        this.security.name = null
        this.security.isin = null
      }
    }
  }
}
</script>
